import Icon, { type IconName } from 'components/Icon';
import LinkOrButton from 'components/LinkOrButton';
import type {
	AnchorHTMLAttributesWithRef,
	ButtonHTMLAttributesWithRef,
} from 'types';
import { cnm } from 'utils/classNames';
import { is } from 'utils/helpers';

export type ButtonSize = 'lg' | 'md' | 'sm';
export type ButtonVariant =
	| 'borderless'
	| 'branded'
	| 'cta'
	| 'secondary'
	| 'text'
	| 'text-inherit'
	| 'text-inline'
	| 'transparent';
export type ButtonDisplayWidth = 'dynamic' | 'full';

export interface BaseProps {
	/** Extra class names */
	className?: string;

	/** Should the button be disabled */
	disabled?: boolean;

	/** Button width */
	displayWidth?: ButtonDisplayWidth;

	/** If the button's hit area should be invisibly expanded outside its borders. */
	hasExpandedHitArea?: boolean;

	/** Icon before the text/content */
	leadingIcon?: IconName;

	/** Extra class names for the leading icon */
	leadingIconClass?: string;

	/** Button size */
	size?: ButtonSize;

	/** Text alignment */
	textAlignment?: 'center' | 'left';

	/** Icon after the text/content */
	trailingIcon?: IconName;

	/** Extra class names for the trailing icon */
	trailingIconClass?: string;

	/** If the text variant should have an underline */
	underline?: boolean;

	/** Button style variant */
	variant?: ButtonVariant;
}

type AnchorAttrs = AnchorHTMLAttributesWithRef<HTMLAnchorElement>;
type ButtonAttrs = ButtonHTMLAttributesWithRef<HTMLButtonElement>;

interface AnchorProps extends AnchorAttrs, BaseProps {
	type?: never;

	/** Link URL, results in an anchor instead of a button */
	href: string;

	/** Link target */
	target?: AnchorAttrs['target'];
}

export interface ButtonProps extends BaseProps, ButtonAttrs {
	href?: never;

	/** Button click handler, required for non-submit button */
	onClick: NonNullable<ButtonAttrs['onClick']>;

	target?: never;
}

interface SubmitProps extends BaseProps, ButtonAttrs {
	type: 'submit';

	href?: never;

	/** Button click handler, optional for submit button */
	onClick?: ButtonAttrs['onClick'];

	target?: never;
}

type Props = AnchorProps | ButtonProps | SubmitProps;

/**
 * Button in different variants and sizes.
 *
 * Requires different props depending on the kind of button:
 * - `href` for links.
 * - `onClick` for 'button buttons'.
 * - `type="submit"` for submit buttons.
 */
export default function Button({
	children,
	className = '',
	disabled = false,
	displayWidth = 'dynamic',
	hasExpandedHitArea = false,
	href,
	leadingIcon,
	leadingIconClass,
	onClick,
	ref,
	size = 'md',
	target,
	textAlignment = 'center',
	trailingIcon,
	trailingIconClass,
	type = 'button',
	underline = true,
	variant = 'secondary',
	...attrs
}: Props) {
	const isTextButton = is.oneOf(variant, 'text', 'text-inline', 'text-inherit');
	const iconSize = size === 'sm' ? 20 : 24;
	return (
		<LinkOrButton
			{...attrs}
			ref={ref}
			href={href}
			disabled={disabled}
			type={type}
			target={target}
			onClick={onClick}
			className={cnm(
				displayWidth === 'full' && 'w-full',

				!isTextButton && [
					displayWidth === 'dynamic' ? 'inline-flex' : 'flex',
					'items-center font-bold',
					textAlignment === 'left'
						? 'justify-start text-left'
						: 'justify-center text-center',

					// Always have a border for forced colors mode.
					'border border-transparent',
					!disabled && 'forced-colors:hover:outline-solid',

					size === 'sm' &&
						'min-h-9 min-w-9 rounded-[1.5rem] px-3 py-1.5 text-sm',
					size === 'md' &&
						'min-h-12 min-w-12 rounded-[1.75rem] px-4 py-2 text-md',
					size === 'lg' &&
						'min-h-14 min-w-14 rounded-[2rem] px-6 py-2.5 text-md',

					// Leading after text size classes, or it gets merged away.
					// https://github.com/dcastil/tailwind-merge/issues/573
					'leading-tight',
				],

				variant === 'branded' && [
					!disabled &&
						'bg-brand text-inverse hover:bg-brandHover active:bg-brandActive',
					disabled && 'bg-disabled text-disabled',
				],

				variant === 'cta' && [
					!disabled &&
						'bg-action text-inverse hover:bg-actionHover active:bg-actionActive',
					disabled && 'bg-disabled text-disabled',
				],

				variant === 'secondary' && [
					'bg-base',
					!disabled &&
						'border-strong text-base hover:bg-baseHover active:bg-baseActive',
					disabled && 'border-subtle text-disabled',
				],

				variant === 'borderless' && [
					'bg-transparent',
					!disabled && 'text-base hover:bg-inverse/20 active:bg-inverse/30',
					disabled && 'text-disabled',
				],

				variant === 'transparent' && [
					'bg-positive/80',
					!disabled && 'text-inverse hover:bg-positive/90 active:bg-positive',
					disabled && 'bg-disabled text-disabled',
				],

				isTextButton && [
					'text-inherit',
					variant === 'text' &&
						'inline-flex min-h-8 items-center justify-center py-1',
					(variant === 'text-inline' || variant === 'text-inherit') &&
						'inline-block text-left',

					variant !== 'text-inherit' && [
						size === 'sm' && 'text-sm',
						size === 'md' && 'text-md',
						size === 'lg' && 'text-md',
						disabled && 'text-disabled',
					],
					variant === 'text-inherit' && 'text-inherit-size',

					underline && ['underline', !disabled && 'hover:underline-strong'],
				],

				'font-body',

				// Only expand vertically, most buttons should naturally be wide enough.
				hasExpandedHitArea &&
					'relative after:absolute after:inset-x-0 after:-top-2 after:-bottom-2',

				className,
			)}
		>
			{leadingIcon && (
				<Icon
					name={leadingIcon}
					size={iconSize}
					className={cnm(size === 'lg' ? 'mr-3' : 'mr-2', leadingIconClass)}
				/>
			)}
			{children}
			{trailingIcon && (
				<Icon
					name={trailingIcon}
					size={iconSize}
					className={cnm(size === 'lg' ? 'ml-3' : 'ml-2', trailingIconClass)}
				/>
			)}
		</LinkOrButton>
	);
}
Button.displayName = 'Button';
